import { useEffect, useMemo, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { PeriodSelector } from "@/components/workflow/common/PeriodSelector";
import { DEFAULT_SPEND_BASED_ENTRY } from "@/contexts/wizard/useScope3State";

const spendCategories = [
  { value: "office_supplies", label: "Office supplies" },
  { value: "it_equipment", label: "IT equipment & electronics" },
  { value: "professional_services", label: "Professional services (consulting, legal, accounting)" },
  { value: "marketing_advertising", label: "Marketing & advertising" },
  { value: "catering_food", label: "Catering & food services" },
  { value: "furniture_fixtures", label: "Furniture & fixtures" },
  { value: "other", label: "Other business expenses" },
];

const coverageProgressMap: Record<string, number> = {
  low: 33,
  medium: 66,
  high: 100,
};

interface Props {
  isOpen: boolean;
}

export default function CompanyExpensesFormSection({ isOpen }: Props) {
  const {
    scope3Data,
    assessment,
    scope3CoverageData,
    currentSpendBasedEntry,
    setCurrentSpendBasedEntry,
    addSpendBasedEntry,
    deleteSpendBasedEntry,
    updateSpendBasedEntry,
    updateScope3Coverage,
    forceSave,
    isLocked,
    isReadOnly,
  } = useWizard();
  const { toast } = useToast();
  const disabled = isLocked || isReadOnly;
  const pendingSaveRef = useRef(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      forceSave();
    }
  }, [scope3Data.spendBasedEntries, scope3CoverageData, forceSave]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentSpendBasedEntry(DEFAULT_SPEND_BASED_ENTRY);
      setEditingEntryId(null);
    }
  }, [isOpen]);

  const handleEditEntry = (entry: typeof scope3Data.spendBasedEntries[0]) => {
    setCurrentSpendBasedEntry({
      subCategory: entry.subCategory,
      description: entry.description,
      spendAmount: entry.spendAmount,
      currency: entry.currency,
      period: entry.period,
    });
    setEditingEntryId(entry.id);
  };

  const handleCancelEdit = () => {
    setCurrentSpendBasedEntry(DEFAULT_SPEND_BASED_ENTRY);
    setEditingEntryId(null);
  };

  const handleUpdateEntry = () => {
    // SYNCHRONOUS — no API call since calculateSpendBasedEmissions returns null
    const id = editingEntryId!;
    updateSpendBasedEntry(id, { ...currentSpendBasedEntry, tco2e: null, calculating: false });
    pendingSaveRef.current = true;
    setCurrentSpendBasedEntry(DEFAULT_SPEND_BASED_ENTRY);
    setEditingEntryId(null);
    toast({ title: "Entry updated" });
  };

  const totalSpend = useMemo(() => {
    return scope3Data.spendBasedEntries.reduce((sum, entry) => sum + (entry.spendAmount || 0), 0);
  }, [scope3Data.spendBasedEntries]);

  const coverageProgress = scope3CoverageData.level ? coverageProgressMap[scope3CoverageData.level] || 0 : 0;
  const completion = scope3Data.spendBasedEntries.length > 0 ? 100 : 0;

  const handleAddEntry = async () => {
    const result = await addSpendBasedEntry();
    if (!result.ok) {
      toast({ title: result.error || "Unable to add spend entry", variant: "destructive" });
      return;
    }
    pendingSaveRef.current = true;
    toast({
      title: "Spend-based entry added",
    });
  };

  return (
    <div className="space-y-6">
      {disabled && <LockedBanner />}

      <div className="form-surface-card">
        <div className="flex flex-col gap-6 p-6">
          <h4 className="text-sm font-bold leading-5 text-gray-800">{"Add Expense Entry"}</h4>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="form-label">{"Category"}</Label>
              <Select
                value={currentSpendBasedEntry.subCategory}
                onValueChange={(value) =>
                  setCurrentSpendBasedEntry((prev) => ({
                    ...prev,
                    category: "spend_based",
                    subCategory: value as typeof currentSpendBasedEntry.subCategory,
                  }))
                }
                disabled={disabled}
              >
                <SelectTrigger className="form-select">
                  <SelectValue placeholder={"Select category"} />
                </SelectTrigger>
                <SelectContent>
                  {spendCategories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="form-label">{"Annual spend (USD)"}</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="0.01"
                value={currentSpendBasedEntry.spendAmount ?? ""}
                onChange={(e) =>
                  setCurrentSpendBasedEntry((prev) => ({
                    ...prev,
                    spendAmount: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
                placeholder={"e.g. 12000"}
                disabled={disabled}
              />
            </div>
            <PeriodSelector
              periodStart={assessment!.periodStart}
              periodEnd={assessment!.periodEnd}
              value={currentSpendBasedEntry.period ?? null}
              onChange={(value) => setCurrentSpendBasedEntry((prev) => ({ ...prev, period: value }))}
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <Label className="form-label">{"Description (optional)"}</Label>
            <Input
              className="form-input"
              value={currentSpendBasedEntry.description || ""}
              onChange={(e) =>
                setCurrentSpendBasedEntry((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder={"e.g. Office stationery and printing"}
              disabled={disabled}
            />
          </div>

          <div>
            {editingEntryId ? (
              <div className="flex gap-2">
                <Button onClick={handleUpdateEntry} disabled={disabled} className="form-add-button">
                  Update entry
                </Button>
                <Button onClick={handleCancelEdit} disabled={disabled} variant="ghost">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button onClick={handleAddEntry} disabled={disabled} className="form-add-button">
                <Plus className="mr-2 h-3 w-3" /> {"Add Entry"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="form-surface-card">
        <div className="flex flex-col gap-6 p-6">
          <h4 className="text-sm font-bold leading-5 text-gray-800">{"Coverage Indicator"}</h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="form-label">{"Coverage level"}</Label>
              <Select
                value={scope3CoverageData.level || ""}
                onValueChange={(value) => {
                  updateScope3Coverage({ level: value as "low" | "medium" | "high" });
                  pendingSaveRef.current = true;
                }}
                disabled={disabled}
              >
                <SelectTrigger className="form-select">
                  <SelectValue placeholder={"Select level"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{"Low"}</SelectItem>
                  <SelectItem value="medium">{"Medium"}</SelectItem>
                  <SelectItem value="high">{"High"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="form-label">{"Justification"}</Label>
              <Textarea
                value={scope3CoverageData.justification || ""}
                onChange={(e) => {
                  updateScope3Coverage({ justification: e.target.value });
                  pendingSaveRef.current = true;
                }}
                placeholder={"Explain data coverage for spend-based categories"}
                className="min-h-[96px] rounded-2xl border-slate-200 bg-slate-50 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-300"
                disabled={disabled}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="form-label">{"Coverage progress"}</Label>
            <Progress value={coverageProgress} className="form-progress" />
            <p className="text-xs leading-4 text-slate-500">
              {`Coverage: ${coverageProgress}%`}
            </p>
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="entries-header">
          <h4 className="entries-title">{"Entries"}</h4>
          <span className="entries-count">
            {`${scope3Data.spendBasedEntries.length} entries`}
          </span>
        </div>

        {scope3Data.spendBasedEntries.length === 0 ? (
          <p className="entries-empty">{"No spend-based entries yet."}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="table-row-base">
                <TableHead className="table-head-base">{"Category"}</TableHead>
                <TableHead className="table-head-base text-center">
                  {"Spend (USD)"}
                </TableHead>
                <TableHead className="table-head-base text-center">{"Period"}</TableHead>
                <TableHead className="table-head-base text-right">{"Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scope3Data.spendBasedEntries.map((entry) => (
                <TableRow
                  key={entry.id}
                  className={`table-row-base${editingEntryId === entry.id ? " outline outline-2 outline-primary/30" : ""}`}
                >
                  <TableCell className="table-cell-base-strong">
                    {spendCategories.find((category) => category.value === entry.subCategory)?.label || ""}
                  </TableCell>
                  <TableCell className="table-cell-base-center">{entry.spendAmount.toFixed(2)}</TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                  </TableCell>
                  <TableCell className="table-cell-base-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditEntry(entry)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      disabled={disabled || (editingEntryId !== null && editingEntryId !== entry.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        deleteSpendBasedEntry(entry.id);
                        pendingSaveRef.current = true;
                      }}
                      className="delete-button"
                      disabled={disabled || editingEntryId !== null}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="total-card">
        <p className="total-label">{"Total Spend"}</p>
        <p className="total-value">${totalSpend.toFixed(2)}</p>
      </div>

      <Progress value={completion} className="form-progress" />
      <p className="text-xs leading-4 text-slate-500">{`Completion: ${completion}%`}</p>
    </div>
  );
}
